import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  DataTable,
  DataTableSkeleton,
  InlineNotification,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
} from '@carbon/react';
import EmptyState from 'src/components/Base/EmptyState';
import { authClient } from 'src/services/auth/client';
import { QUERIES } from 'src/utils/react-query';

type AdminOrganization = {
  id: string;
  name: string;
  slug: string;
  memberCount: number;
  ownerUserId: string | null;
  createdAt: string;
};

const AdminOrganizations = React.memo(() => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  function mutationError(e: unknown): string {
    return e instanceof Error && e.message ? e.message : t('admin.error', { message: '' });
  }

  const { data, isLoading } = useQuery({
    queryKey: [QUERIES.ADMIN_ORGANIZATIONS],
    queryFn: async () => {
      const response = await fetch(`${import.meta.env.VITE_AUTH_URL}/internal/organizations`, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error(`Organizations request failed: ${response.status}`);
      return (await response.json()) as { data: AdminOrganization[]; total: number };
    },
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: [QUERIES.ADMIN_ORGANIZATIONS] });
  const suspend = useMutation({
    mutationFn: async (userId: string) => {
      setError(null);
      try {
        const { error: apiError } = await authClient.admin.banUser({
          userId,
          banReason: t('admin.banReason'),
        });
        if (apiError) throw new Error(apiError.message);
      } catch (e) {
        setError(mutationError(e));
        throw e;
      }
    },
    onSuccess: invalidate,
  });
  const restore = useMutation({
    mutationFn: async (userId: string) => {
      setError(null);
      try {
        const { error: apiError } = await authClient.admin.unbanUser({ userId });
        if (apiError) throw new Error(apiError.message);
      } catch (e) {
        setError(mutationError(e));
        throw e;
      }
    },
    onSuccess: invalidate,
  });

  const headers = [
    { key: 'name', header: t('common.name') },
    { key: 'slug', header: t('admin.columns.slug') },
    { key: 'members', header: t('admin.columns.members') },
    { key: 'createdAt', header: t('admin.columns.createdAt') },
  ];

  if (isLoading) {
    return <DataTableSkeleton columnCount={5} rowCount={5} headers={headers} />;
  }

  const rows =
    data?.data.map((organization) => ({
      id: organization.id,
      name: organization.name,
      slug: organization.slug,
      members: organization.memberCount,
      createdAt: dayjs(organization.createdAt).format('DD/MM/YYYY'),
    })) ?? [];

  return (
    <>
      {error && <InlineNotification kind="error" title={error} lowContrast hideCloseButton />}
      <DataTable rows={rows} headers={headers}>
        {({ rows, headers: tableHeaders, getTableProps, getHeaderProps, getRowProps }) => (
          <TableContainer title={t('admin.organizations.title')} description={t('admin.organizations.subtitle')}>
            <Table {...getTableProps()}>
              <TableHead>
                <TableRow>
                  {tableHeaders.map((header) => (
                    <TableHeader key={header.key} {...getHeaderProps({ header })}>
                      {header.header}
                    </TableHeader>
                  ))}
                  <TableHeader>{t('common.actions')}</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, index) => {
                  const organization = data?.data[index];
                  return (
                    <TableRow key={row.id} {...getRowProps({ row })}>
                      {row.cells.map((cell) => (
                        <TableCell key={cell.id}>{cell.value}</TableCell>
                      ))}
                      <TableCell>
                        {organization?.ownerUserId && (
                          <>
                            <Button
                              kind="danger--ghost"
                              size="sm"
                              disabled={suspend.isPending}
                              onClick={() => suspend.mutate(organization.ownerUserId!)}
                            >
                              {t('admin.ban')}
                            </Button>
                            <Button
                              kind="ghost"
                              size="sm"
                              disabled={restore.isPending}
                              onClick={() => restore.mutate(organization.ownerUserId!)}
                            >
                              {t('admin.unban')}
                            </Button>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {rows.length === 0 && <EmptyState />}
          </TableContainer>
        )}
      </DataTable>
    </>
  );
});

export default AdminOrganizations;
